import {useEffect,useCallback,useRef} from 'react'
import {rt} from '../helpers/firebase'
import {ref,onValue,set,remove,onDisconnect} from 'firebase/database'

const PRESENCE_COLORS=[
    '#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316'
]

function getPresenceColor(uid) {
    // Deterministic color based on uid
    let hash=0
    for(let i=0;i<uid.length;i++) hash=(hash*31+uid.charCodeAt(i))>>>0
    return PRESENCE_COLORS[hash%PRESENCE_COLORS.length]
}

/**
 * usePresence — writes/reads collaborative presence data to RTDB.
 * Structure: presence/${ownerUid}/${projectId}/${myUid} = {displayName, color, selectedNodes, lastSeen}
 * NEVER stores presence in node.data — this hook handles it separately.
 */
export default function usePresence({ownerUid,projectId,myUid,displayName,enabled=true}) {
    const myRef=useRef(null)
    const collaboratorsRef=useRef({})

    useEffect(()=>{
        if(!ownerUid||!projectId||!myUid||!enabled) return

        const presencePath=`presence/${ownerUid}/${projectId}/${myUid}`
        myRef.current=ref(rt,presencePath)

        const initialData={
            displayName: displayName||'Unknown',
            color: getPresenceColor(myUid),
            selectedNodes: [],
            lastSeen: Date.now()
        }

        set(myRef.current,initialData)
        onDisconnect(myRef.current).remove()

        return ()=>{
            if(myRef.current) remove(myRef.current)
        }
    },[ownerUid,projectId,myUid,displayName,enabled])

    const updateSelectedNodes=useCallback((nodeIds)=>{
        if(!myRef.current) return
        set(myRef.current,{
            displayName: displayName||'Unknown',
            color: getPresenceColor(myUid),
            selectedNodes: nodeIds||[],
            lastSeen: Date.now()
        })
    },[myUid,displayName])

    const subscribeToCollaborators=useCallback((onChange)=>{
        if(!ownerUid||!projectId) return ()=>{}
        const presenceRef=ref(rt,`presence/${ownerUid}/${projectId}`)
        const unsub=onValue(presenceRef,(snap)=>{
            if(!snap.exists()) {onChange({}); return}
            const data=snap.val()
            // Exclude self
            const others=Object.fromEntries(
                Object.entries(data).filter(([uid])=>uid!==myUid)
            )
            collaboratorsRef.current=others
            onChange(others)
        })
        return unsub
    },[ownerUid,projectId,myUid])

    return {updateSelectedNodes,subscribeToCollaborators,getPresenceColor}
}
